import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
}

const features = [
  {
    icon: 'ri-code-s-slash-line',
    title: 'Desarrollo a Medida',
    description: 'Software robusto diseñado para eliminar procesos manuales y errores operativos. Creamos soluciones enterprise que se adaptan exactamente a tus necesidades de negocio.',
    keywords: 'desarrollo software a medida, desarrollo software empresarial, software personalizado Colombia'
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Apps Móviles',
    description: 'Interfaces modernas en iOS y Android que conectan tu negocio con el usuario final. Desarrollo de aplicaciones móviles nativas y cross-platform.',
    keywords: 'desarrollo app móvil, aplicaciones iOS Android, desarrollo mobile Colombia'
  },
  {
    icon: 'ri-stack-line',
    title: 'Integraciones & APIs',
    description: 'Conectamos tus sistemas para eradicate las "Tareas Zombi" y automatizar el flujo de datos. APIs RESTful y integraciones con cualquier plataforma.',
    keywords: 'integración de sistemas, desarrollo API, automatización de procesos'
  },
  {
    icon: 'ri-pantone-line',
    title: 'Diseño UI/UX',
    description: 'Experiencias intuitivas enfocadas en la conversión y la facilidad de uso tecnológico. Diseño de interfaces que maximizan la experiencia de usuario.',
    keywords: 'diseño UI UX, diseño de interfaces, experiencia de usuario'
  },
  {
    icon: 'ri-cloud-line',
    title: 'Arquitectura Cloud',
    description: 'Soluciones escalables en AWS y Azure que crecen al ritmo de tu demanda real. Migración a la nube y arquitectura serverless.',
    keywords: 'cloud computing AWS Azure, arquitectura cloud, migración a la nube'
  },
  {
    icon: 'ri-shield-keyhole-line',
    title: 'Ciberseguridad',
    description: 'Blindaje de datos y sistemas bajo estándares de protección de grado empresarial. Auditorías de seguridad y cumplimiento normativo.',
    keywords: 'ciberseguridad empresarial, protección de datos, seguridad informática'
  }
];

export default function Features() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Asegurar que el canvas ocupe toda la sección
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement?.offsetHeight || 900;
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    // MÁS PARTÍCULAS Y MÁS VISIBLES (60 partículas a 0.25 opacidad)
    particlesRef.current = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.35, 
      vy: (Math.random() - 0.5) * 0.35,
      radius: Math.random() * 1.8 + 0.2, // Tamaños más variados
      color: '#00C2FF'
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach(p => {
        p.x = (p.x + p.vx + canvas.width) % canvas.width;
        p.y = (p.y + p.vy + canvas.height) % canvas.height;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.25; // Opacidad duplicada para visibilidad
        ctx.fill();
      });
      requestAnimationFrame(draw);
    };
    draw();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section id="solutions" className="py-16 sm:py-24 lg:py-32 bg-[#050608] relative overflow-hidden">
      {/* Canvas de partículas de fondo ocupando todo el espacio */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />

      {/* Orbes de luz intensificados */}
      <div className="absolute top-0 right-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#1A73E8]/8 blur-[120px] rounded-full" />
      <div className="absolute bottom-0 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#00C2FF]/8 blur-[120px] rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="text-center mb-14 sm:mb-20 lg:mb-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full backdrop-blur-md mb-6 sm:mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00C2FF] animate-pulse"></span>
            <span className="text-white text-[11px] font-bold uppercase tracking-[0.2em]">Soluciones</span>
          </div>
          
          <h2 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tighter mb-5 sm:mb-8 leading-[1.1]">
            Especialistas en <br /> 
            {/* CORRECCIÓN: Palabra Tecnología en azul */}
            <span className="text-[#00C2FF]">Tecnología.</span>
          </h2>
          
          <p className="text-base sm:text-lg lg:text-xl text-[#86868b] max-w-2xl mx-auto leading-relaxed px-2">
            Convertimos la complejidad técnica en herramientas simples y potentes para tu negocio.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group p-6 sm:p-8 lg:p-10 rounded-[2rem] sm:rounded-[2.5rem] bg-[#0d1117]/60 border border-white/5 hover:border-[#00C2FF]/40 transition-all duration-500 backdrop-blur-sm shadow-2xl relative"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-xl sm:rounded-2xl bg-[#00C2FF]/10 border border-[#00C2FF]/30 mb-5 sm:mb-8 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                <i className={`${feature.icon} text-xl sm:text-2xl text-[#00C2FF]`}></i>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 group-hover:text-[#00C2FF] transition-colors">
                {feature.title}
              </h3>

              <p className="text-[#86868b] text-sm sm:text-[15px] leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}